import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import api from "../lib/api";
import { triggerBlobDownload } from "../lib/download";
import { useAuth } from "../App";
import { SendingSchoolsTab } from "../components/contacts/SendingSchoolsTab";
import { TransportProvidersTab } from "../components/contacts/TransportProvidersTab";

const CATEGORIES = [
  { value: "district", label: "District" },
  { value: "community", label: "Community" },
  { value: "tms", label: "School" },
  { value: "general", label: "General" },
];

const ContactRow = ({ contact, onEdit, onDelete, onViewComms }) => (
  <tr className="border-b hover:bg-gray-50" data-testid={`contact-row-${contact.id}`}>
    <td className="px-3 py-2 font-medium text-sm">{contact.name}</td>
    <td className="px-3 py-2 text-sm text-muted-foreground">{contact.title}</td>
    <td className="px-3 py-2 text-sm">{contact.organization}</td>
    <td className="px-3 py-2 text-sm">
      {contact.phone && <a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline">{contact.phone}</a>}
    </td>
    <td className="px-3 py-2 text-sm">
      {contact.email && <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">{contact.email}</a>}
    </td>
    <td className="px-3 py-2 text-sm text-muted-foreground truncate max-w-[150px]">{contact.notes}</td>
    <td className="px-3 py-2 text-right">
      <div className="flex gap-1 justify-end">
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onViewComms(contact)} data-testid={`view-comms-${contact.id}`}>Comm Log</Button>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onEdit(contact)} data-testid={`edit-contact-${contact.id}`}>Edit</Button>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-600 hover:bg-red-50" onClick={() => onDelete(contact)} data-testid={`delete-contact-${contact.id}`}>Del</Button>
      </div>
    </td>
  </tr>
);

export default function ContactsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const validTabs = ["district", "community", "tms", "general", "sending-schools", "transport-providers"];
  const [activeTab, setActiveTab] = useState(validTabs.includes(tabParam) ? tabParam : "district");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [sortBy, setSortBy] = useState("name");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({ name: "", title: "", organization: "", category: "district", phone: "", email: "", address: "", notes: "" });
  const [filterTitle, setFilterTitle] = useState("all");
  const fileInputRef = useRef(null);

  const fetchContacts = async () => {
    try {
      const res = await api.get("/contacts");
      setContacts(res.data);
    } catch (err) {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContacts(); }, []);

  const filteredContacts = contacts
    .filter(c => c.category === activeTab)
    .filter(c => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return c.name.toLowerCase().includes(q) || (c.organization || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.title || "").toLowerCase().includes(q);
    })
    .filter(c => filterTitle === "all" || (c.title || "").toLowerCase() === filterTitle.toLowerCase())
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "title") return (a.title || "").localeCompare(b.title || "");
      if (sortBy === "organization") return (a.organization || "").localeCompare(b.organization || "");
      return 0;
    });

  // Get unique titles for the active tab
  const titleOptions = [...new Set(contacts.filter(c => c.category === activeTab).map(c => c.title).filter(Boolean))].sort();

  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({ name: contact.name, title: contact.title || "", organization: contact.organization || "", category: contact.category, phone: contact.phone || "", email: contact.email || "", address: contact.address || "", notes: contact.notes || "" });
    } else {
      setEditingContact(null);
      setFormData({ name: "", title: "", organization: "", category: activeTab, phone: "", email: "", address: "", notes: "" });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editingContact) {
        await api.put(`/contacts/${editingContact.id}`, formData);
        toast.success("Contact updated");
      } else {
        await api.post("/contacts", formData);
        toast.success("Contact added");
      }
      setDialogOpen(false);
      fetchContacts();
    } catch (err) {
      toast.error("Failed to save contact");
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Delete contact "${contact.name}"?`)) return;
    try {
      await api.delete(`/contacts/${contact.id}`);
      toast.success("Contact deleted");
      fetchContacts();
    } catch (err) {
      toast.error("Failed to delete contact");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message);
      fetchContacts();
    } catch (err) {
      toast.error("Failed to import contacts");
    }
    e.target.value = "";
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/contacts/export?category=${activeTab}`, { responseType: "blob" });
      triggerBlobDownload(new Blob([res.data]), `contacts_${activeTab}.xlsx`);
    } catch (err) {
      toast.error("Failed to export contacts");
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const rows = filteredContacts.map(c =>
      `<tr><td style="border:1px solid #ddd;padding:6px">${c.name}</td><td style="border:1px solid #ddd;padding:6px">${c.title || ""}</td><td style="border:1px solid #ddd;padding:6px">${c.organization || ""}</td><td style="border:1px solid #ddd;padding:6px">${c.phone || ""}</td><td style="border:1px solid #ddd;padding:6px">${c.email || ""}</td><td style="border:1px solid #ddd;padding:6px">${c.notes || ""}</td></tr>`
    ).join("");
    printWindow.document.write(`<html><head><title>Contacts - ${CATEGORIES.find(c => c.value === activeTab)?.label}</title><style>body{font-family:Arial,sans-serif;font-size:12px}table{border-collapse:collapse;width:100%}th{background:#00305E;color:white;padding:8px;text-align:left}td{padding:6px}</style></head><body><h2>Contacts - ${CATEGORIES.find(c => c.value === activeTab)?.label}</h2><table><thead><tr><th>Name</th><th>Title</th><th>Organization</th><th>Phone</th><th>Email</th><th>Notes</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const catCount = (cat) => contacts.filter(c => c.category === cat).length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00305E]" /></div>;

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#00305E]">Contacts</h1>
          <p className="text-muted-foreground">{contacts.length} total contacts</p>
        </div>
        <div className="flex gap-2 flex-wrap" style={{ display: activeTab === "sending-schools" ? "none" : undefined }}>
          <input type="file" ref={fileInputRef} onChange={handleImport} className="hidden" accept=".xlsx,.xls" />
          <Button variant="outline" onClick={async () => {
            try {
              const res = await api.get("/contacts/template", { responseType: "blob" });
              triggerBlobDownload(new Blob([res.data]), "contacts_import_template.xlsx");
            } catch { toast.error("Failed to download template"); }
          }} data-testid="download-template-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            Download Template
          </Button>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-contacts-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import Excel
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-contacts-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </Button>
          <Button variant="outline" onClick={handlePrint} data-testid="print-contacts-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/></svg>
            Print
          </Button>
          <Button onClick={() => handleOpenDialog()} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-contact-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Contact
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="contacts-tabs">
          {CATEGORIES.map(cat => (
            <TabsTrigger key={cat.value} value={cat.value} data-testid={`tab-contacts-${cat.value}`}>
              {cat.label} ({catCount(cat.value)})
            </TabsTrigger>
          ))}
          <TabsTrigger value="sending-schools" data-testid="tab-contacts-sending-schools">Sending School Set Up</TabsTrigger>
          <TabsTrigger value="transport-providers" data-testid="tab-contacts-transport-providers">Transportation Providers</TabsTrigger>
        </TabsList>

        {CATEGORIES.map(cat => (
          <TabsContent key={cat.value} value={cat.value}>
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle>{cat.label} Contacts</CardTitle>
                    <CardDescription>{filteredContacts.length} contacts</CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Input placeholder="Search contacts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      className="w-64" data-testid="search-contacts-input" />
                    {titleOptions.length > 0 && (
                      <Select value={filterTitle} onValueChange={v => setFilterTitle(v)}>
                        <SelectTrigger className="w-44" data-testid="filter-title-select"><SelectValue placeholder="All Titles" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Titles</SelectItem>
                          {titleOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-40" data-testid="sort-contacts-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">Sort by Name</SelectItem>
                        <SelectItem value="title">Sort by Title</SelectItem>
                        <SelectItem value="organization">Sort by Organization</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredContacts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="contacts-table">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="px-3 py-2 text-left font-semibold">Name</th>
                          <th className="px-3 py-2 text-left font-semibold">Title</th>
                          <th className="px-3 py-2 text-left font-semibold">Organization</th>
                          <th className="px-3 py-2 text-left font-semibold">Phone</th>
                          <th className="px-3 py-2 text-left font-semibold">Email</th>
                          <th className="px-3 py-2 text-left font-semibold">Notes</th>
                          <th className="px-3 py-2 text-right font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredContacts.map(c => <ContactRow key={c.id} contact={c} onEdit={handleOpenDialog} onDelete={handleDelete} onViewComms={(ct) => navigate(`/communications?contact=${encodeURIComponent(ct.name)}`)} />)}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No {cat.label.toLowerCase()} contacts yet. Click &quot;Add Contact&quot; to get started.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
        <TabsContent value="sending-schools">
          <SendingSchoolsTab />
        </TabsContent>
        <TabsContent value="transport-providers">
          <TransportProvidersTab />
        </TabsContent>
      </Tabs>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" aria-describedby="contact-dialog-desc">
          <DialogHeader>
            <DialogTitle>{editingContact ? "Edit Contact" : "Add Contact"}</DialogTitle>
            <p id="contact-dialog-desc" className="text-sm text-muted-foreground">Fill in the contact information below</p>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Full name" data-testid="contact-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} placeholder="e.g., Director, Counselor" data-testid="contact-title-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Organization</Label>
                <Input value={formData.organization} onChange={e => setFormData(p => ({ ...p, organization: e.target.value }))} placeholder="Organization name" data-testid="contact-org-input" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={v => setFormData(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="contact-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="555-123-4567" data-testid="contact-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" data-testid="contact-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={formData.address} onChange={e => setFormData(p => ({ ...p, address: e.target.value }))} placeholder="Full address" data-testid="contact-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes" data-testid="contact-notes-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-contact-btn">
                {editingContact ? "Save Changes" : "Add Contact"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
